// P1 (2026-07-14) — Compact user chip used by Compliance Procedures and
// (later) any list view that needs to show "who" instead of a UUID.
//
// Renders three tiers of density:
//   1. `user` present         -> avatar circle + name + email + role pill
//   2. `user == null` (nullable)-> muted "— Unassigned" + optional "+ Assign"
//   3. `user == undefined`    -> "—" placeholder (owner column, older rows)
//
// Kept intentionally small: no drawer, no dropdown; the assign flow is
// composed by the caller (e.g. CompliancePage) so this component stays
// reusable.
import { UserPlus } from "lucide-react";

const ROLE_COLORS = {
  system_admin:                { bg: "bg-slate-900",   text: "text-white",       label: "Admin" },
  salihi_finance_manager:      { bg: "bg-indigo-100",  text: "text-indigo-700",  label: "Finance Mgr" },
  salihi_senior_accountant:    { bg: "bg-sky-100",     text: "text-sky-700",     label: "Sr. Acct" },
  salihi_accountant:           { bg: "bg-sky-50",      text: "text-sky-700",     label: "Accountant" },
  salihi_compliance_officer:   { bg: "bg-emerald-100", text: "text-emerald-700", label: "Compliance" },
  internal_auditor:            { bg: "bg-amber-100",   text: "text-amber-700",   label: "Auditor" },
  somturk_data_entry:          { bg: "bg-slate-100",   text: "text-slate-700",   label: "Data Entry" },
  somturk_observer:            { bg: "bg-slate-100",   text: "text-slate-500",   label: "Observer" },
};

function initialsOf(u) {
  if (u.initials) return u.initials;
  const src = (u.full_name || u.email || "?").split("@")[0];
  const parts = src.replace(/[._-]/g, " ").split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
}

export function UserChip({ user, onAssign, size = "sm", testid, showEmail = true }) {
  // Unassigned (nullable) — offer assign button when handler provided.
  if (user === null) {
    return (
      <span
        className="inline-flex items-center gap-2 text-xs text-slate-400"
        data-testid={testid ? `${testid}-unassigned` : "user-chip-unassigned"}
      >
        <span className="italic">— Unassigned</span>
        {onAssign && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAssign(); }}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] font-medium text-[#4FA3D1] hover:bg-[#4FA3D1]/5 transition"
            data-testid={testid ? `${testid}-assign-btn` : "user-chip-assign-btn"}
          >
            <UserPlus className="h-3 w-3" /> Assign
          </button>
        )}
      </span>
    );
  }
  // Missing (owner never set / user deleted).
  if (!user) return <span className="text-xs text-slate-400" data-testid={testid ? `${testid}-none` : "user-chip-none"}>—</span>;

  const initials = initialsOf(user);
  const role = ROLE_COLORS[user.role] || { bg: "bg-slate-100", text: "text-slate-700", label: user.role || "" };
  const avatarSize = size === "md" ? "h-8 w-8 text-[11px]" : "h-7 w-7 text-[10px]";
  return (
    <span
      className="inline-flex items-center gap-2 min-w-0"
      title={`${user.full_name || ""} · ${user.email || ""}`}
      data-testid={testid || "user-chip"}
    >
      <span
        className={`inline-flex items-center justify-center rounded-full bg-[#4FA3D1]/10 text-[#0B1929] font-semibold shrink-0 ${avatarSize}`}
      >
        {initials}
      </span>
      <span className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-slate-800 truncate leading-tight" data-testid={testid ? `${testid}-name` : undefined}>
          {user.full_name || user.email}
        </span>
        {showEmail && user.email && user.full_name && (
          <span className="text-[11px] text-slate-500 truncate leading-tight">{user.email}</span>
        )}
      </span>
      {role.label && (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${role.bg} ${role.text}`}
          data-testid={testid ? `${testid}-role` : undefined}
        >
          {role.label}
        </span>
      )}
    </span>
  );
}

export default UserChip;
